import { Button } from '@cian/ui-kit/button';
import { Input } from '@cian/ui-kit/input';
import { Link } from '@cian/ui-kit/link';
import { Body2, Caption, UIHeading4 } from '@cian/ui-kit/typography';
import { type FormEvent, useState } from 'react';

import { isEmail } from 'shared/utils/validation';

import style from './style.css';

const RULES_URL = 'https://www.cian.ru/help/about/rules/';

export interface IOffersSubscriptionProps {
  /** «В этом ТЦ пока нет предложений на продажу» */
  title: string;
  onSubscribe(email: string): Promise<void>;
}

type TState = 'form' | 'sending' | 'success';

/** Пустая сделка: подписка на новые предложения (`serp_subscribe_panel.tsx` и `subscription_form` монолита) */
export const OffersSubscription = ({ title, onSubscribe }: IOffersSubscriptionProps) => {
  const [email, setEmail] = useState('');
  const [state, setState] = useState<TState>('form');
  const [error, setError] = useState<string | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!email) {
      setError('Введите адрес');

      return;
    }

    if (!isEmail(email)) {
      setError('Введите корректный адрес');
      setIsInvalid(true);

      return;
    }

    setError(null);
    setState('sending');

    try {
      await onSubscribe(email);
      setState('success');
    } catch {
      setError('Не удалось оформить подписку. Попробуйте снова.');
      setState('form');
    }
  };

  const handleReset = () => {
    setEmail('');
    setError(null);
    setIsInvalid(false);
    setState('form');
  };

  const isSending = state === 'sending';

  return (
    <div className={style['container']} data-name="OffersSubscription">
      <UIHeading4 as="h3">{title}</UIHeading4>
      <Body2>Подпишитесь на обновления, и мы сообщим, когда подходящие предложения появятся.</Body2>
      {state === 'success' ? (
        <div className={style['success']}>
          <Body2>
            На <b>{email}</b> отправлено письмо для активации подписки.
          </Body2>
          <Button theme="main_secondary" size="XS" onClick={handleReset}>
            Выбрать другой адрес
          </Button>
        </div>
      ) : (
        <form className={style['form']} noValidate onSubmit={handleSubmit}>
          <div className={style['fields']}>
            <Input
              size="XS"
              width={210}
              value={email}
              placeholder="Ваш email"
              invalid={isInvalid}
              disabled={isSending}
              data-name="OffersSubscriptionEmail"
              onChange={(_, value) => {
                setEmail(value);
                setIsInvalid(false);
              }}
            />
            <Button theme="main_primary" size="XS" type="submit" loading={isSending} disabled={isSending}>
              Подписаться
            </Button>
          </div>
          {error && (
            <Caption color="text-negative-default" data-name="OffersSubscriptionError">
              {error}
            </Caption>
          )}
          <Caption color="text-secondary-default">
            При подписке вы принимаете условия{' '}
            <Link href={RULES_URL} target="_blank" rel="nofollow" fontSize="XS">
              Пользовательского соглашения
            </Link>
          </Caption>
        </form>
      )}
    </div>
  );
};
